'use client';

import { useEffect, useRef, useState, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { SlideAccordion } from '@/components/molecules/SlideAccordion';
import { SearchForm } from '@/components/molecules/SearchForm';
import { AnimatedFooter } from '@/components/molecules/AnimatedFooter';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { navigationData } from '@/lib/navigationData';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import styles from './MobileMenu.module.scss';

export interface MobileMenuProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

const MobileMenuComponent = ({ isOpen, onClose }: MobileMenuProps) => {
  const pathname = usePathname();
  const menuRef = useRef<HTMLDivElement>(null);
  const [hasMounted, setHasMounted] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  
  // Custom hooks
  useLockBodyScroll(isOpen);
  useFocusTrap(isOpen, menuRef);
  
  // Mount on client
  useEffect(() => {
    setHasMounted(true);
  }, []);
  
  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isSearchOpen) {
          setIsSearchOpen(false);
        } else {
          onClose();
        }
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, isSearchOpen]);
  
  // Reset search when menu closes
  useEffect(() => {
    if (!isOpen) {
      setIsSearchOpen(false);
    }
  }, [isOpen]);
  
  const handleLinkClick = useCallback(() => {
    // Close menu after a delay to allow navigation to complete
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);
  
  
  if (!hasMounted) {
    return null;
  }
  
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            aria-hidden="true"
          />
          
          {/* Menu Panel */}
          <motion.nav
            ref={menuRef}
            className={styles.menu}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ 
              type: 'spring',
              stiffness: 400,
              damping: 40
            }}
            role="dialog"
            aria-modal="true"
            aria-label="モバイルナビゲーション"
          >
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerRow}>
                {/* Search Bar */}
                <div className={styles.searchWrapper}>
                <AnimatePresence mode="wait">
                  {isSearchOpen ? (
                    <motion.div
                      key="search-form"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <SearchForm 
                        placeholder="サイト内を検索..."
                        onSearch={(query) => {
                          // 検索ページへ遷移
                          window.location.href = `/search/?q=${encodeURIComponent(query)}`;
                          setIsSearchOpen(false);
                          onClose();
                        }}
                        onBlur={() => setIsSearchOpen(false)}
                        aria-label="サイト内検索"
                      />
                    </motion.div>
                  ) : (
                    <motion.button
                      key="search-button"
                      className={styles.searchButton}
                      onClick={() => setIsSearchOpen(true)}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      aria-label="検索を開く"
                    >
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path 
                          d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16z" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                        <path 
                          d="M19 19l-4.35-4.35" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>検索</span>
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
                
              <button
                onClick={onClose}
                className={styles.closeButton}
                aria-label="メニューを閉じる"
              >
                <span className={styles.closeIcon}>
                  <span />
                  <span />
                </span>
              </button>
            </div>
          </div>
            
            {/* Current Page Indicator */}
            {pathname !== '/' && (
              <div className={styles.currentPage}>
                <span className={styles.currentPageLabel}>現在のページ</span>
                <span className={styles.currentPagePath}>
                  {pathname.split('/').filter(Boolean).join(' › ')}
                </span>
              </div>
            )}
            
            {/* Navigation Items */}
            <div className={styles.content}>
              <div className={styles.navItems}>
                <SlideAccordion
                  items={navigationData}
                  onLinkClick={handleLinkClick}
                />
              </div>
            </div>
            
            {/* Animated Footer */}
            <AnimatedFooter />
          </motion.nav>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default memo(MobileMenuComponent);